import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { subscribeToAgentResults } from '../../../services/mqtt/mqttService';
import { useTenant } from '@libs/contexts/TenantContext';
import { deviceApi as deviceApiFixed } from '../../../services/eventSystemApiServiceFixed';
import { useEventSystemData } from '../../eventSystem/EventSystemProviderFixed';

const normalizeMacForMatch = (mac) => {
  if (!mac || typeof mac !== 'string') return '';
  return mac.replace(/[^a-fA-F0-9]/g, '').toLowerCase();
};

/**
 * Agent Results Panel Component
 * Displays real-time task/result messages from agents
 *
 * @see Section 3.D of AGENT_API_CONTRACT.md for result message schema
 */
const AgentResultsPanel = ({ agentId }) => {
  // Load results from localStorage on component mount
  const loadResultsFromStorage = () => {
    try {
      const storageKey = `agentResults_${agentId}`;
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to load results from localStorage:', error);
      return [];
    }
  };

  // Save results to localStorage
  const saveResultsToStorage = (results) => {
    try {
      const storageKey = `agentResults_${agentId}`;
      localStorage.setItem(storageKey, JSON.stringify(results));
    } catch (error) {
      console.warn('Failed to save results to localStorage:', error);
    }
  };

  const [results, setResults] = useState(loadResultsFromStorage);
  const [isConnected, setIsConnected] = useState(false);
  const resultsEndRef = useRef(null);
  const { selectedTenant } = useTenant();
  const [addingMap, setAddingMap] = useState({}); // ip -> 'adding'|'added'
  const { refreshData } = (() => {
    try {
      return useEventSystemData();
    } catch {
      return { refreshData: null };
    }
  })();

  useEffect(() => {
    let unsubscribe = null;

    const setupSubscription = async () => {
      try {
        unsubscribe = await subscribeToAgentResults(agentId, ({ topic, data }) => {
          try {
            // Normalize agent result payloads to panel model
            const raw = data;
            if (!raw || typeof raw !== 'object') return;

            const normalized = {
              taskId: raw.commandId || raw.type || 'unknown',
              success: raw.status === 'success' || raw.status === 'accepted',
              output: raw.message || raw.error || (raw.data ? JSON.stringify(raw.data) : ''),
              type: raw.type || null,
              data: raw.data || null,
              timestamp: raw.timestamp || new Date().toISOString(),
            };

            // When a new network-scan result arrives, reset added/adding state so
            // devices can be re-added after deletion from the Event System.
            if (normalized.type === 'network-scan') {
              setAddingMap({});
              try {
                // Auto-sync scan results into Event System Devices using MAC-based upsert
                const tenantId = selectedTenant?.id;
                if (tenantId && normalized.data && Array.isArray(normalized.data.results)) {
                  const results = normalized.data.results;

                  // Build a list of devices from scan results with normalized MACs
                  const scannedDevices = results.map(d => {
                    const rawMac = d.mac || d.macAddress || d.mac_address || '';
                    const macAddress = rawMac || '';
                    const matchKey = normalizeMacForMatch(rawMac);
                    return {
                      macAddress,
                      matchKey,
                      raw: d,
                    };
                  });

                  // If no MACs present, skip auto-sync to avoid creating duplicates by IP-only
                  const anyWithMac = scannedDevices.some(sd => sd.matchKey && sd.matchKey.length > 0);
                  if (!anyWithMac) {
                    return;
                  }

                  // Fetch existing devices once for MAC-based upsert
                  deviceApiFixed
                    .getAll(tenantId)
                    .then(res => {
                      const existing = Array.isArray(res?.data) ? res.data : [];

                      // Build lookup by canonical mac_address (backend field)
                      const byMac = new Map();
                      for (const dev of existing) {
                        const macExisting = dev.mac_address || dev.macAddress || dev.mac || '';
                        const key = normalizeMacForMatch(macExisting);
                        if (key) {
                          byMac.set(key, dev);
                        }
                      }

                      // For each scanned device with MAC, create or update
                      scannedDevices
                        .filter(sd => sd.matchKey && sd.matchKey.length > 0)
                        .forEach(sd => {
                          const d = sd.raw;
                          const matchKey = sd.matchKey;
                          const existingDev = byMac.get(matchKey);

                          const canonicalMac = normalizeMacForMatch(
                            d.mac || d.macAddress || d.mac_address || sd.macAddress || ''
                          ).toUpperCase();

                          if (!canonicalMac) {
                            return;
                          }

                          // Build base payload compatible with backend device create/update
                          const basePayload = {
                            tenantId,
                            name: d.hostname || d.model || 'Algo Device',
                            type: 'algo',
                            status: 'active',
                            model: d.model || '',
                            // IP aliases
                            ip_address: d.ip || '',
                            ipAddress: d.ip || '',
                            ip: d.ip || '',
                            // MAC aliases
                            mac: canonicalMac,
                            macAddress: canonicalMac,
                            mac_address: canonicalMac,
                            // Agent association (for config pulls) but not shown in devices table
                            agentId: agentId,
                            // Segment info from agent discovery
                            segment: d.segment || '',
                            discoveredByAgent: d.discoveredByAgent || agentId,
                            capabilities: ['algo', 'inventory'],
                            targets: {},
                          };

                          if (existingDev && existingDev.id) {
                            // Update existing device by ID
                            deviceApiFixed
                              .update(existingDev.id, basePayload, tenantId)
                              .catch(() => {
                                // Non-blocking; errors are logged in service layer
                              });
                          } else {
                            // Create new device for this MAC
                            deviceApiFixed
                              .create(basePayload, tenantId)
                              .catch(() => {
                                // Non-blocking; errors are logged in service layer
                              });
                          }
                        });
                    })
                    .catch(() => {
                      // Swallow errors here to avoid breaking the UI; user can still manually add devices
                    });
                }
              } catch (_) {
                // Best-effort auto-sync; ignore failures
              }
            }

            setResults(prev => {
              const newResults = [normalized, ...prev].slice(0, 50);
              saveResultsToStorage(newResults);
              return newResults;
            });
            setIsConnected(true);
          } catch (err) {
            console.error('Error processing MQTT result:', err);
          }
        });
        // If subscribe succeeded, mark connected silently
        setIsConnected(true);
      } catch (error) {
        console.error(`Failed to subscribe to agent ${agentId} results:`, error);
        toast.error(`Failed to connect to agent ${agentId} results: ${error.message}`);
        setIsConnected(false);
      }
    };

    setupSubscription();

    return () => {
      try {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      } catch (e) {
        console.error('Error during MQTT unsubscribe cleanup:', e);
      }
    };
  }, [agentId]);

  // Format timestamp
  const formatTimestamp = timestamp => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Try to parse JSON that may be embedded as a string in `output`
  const parseMaybeJson = str => {
    if (!str || typeof str !== 'string') return null;
    const s = str.trim();
    if (!(s.startsWith('{') || s.startsWith('['))) return null;
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };

  // Clear results
  const clearResults = () => {
    setResults([]);
    saveResultsToStorage([]); // Clear from localStorage too
    setAddingMap({}); // Allow re-adding devices after clearing
  };

  // Add discovered Algo device to tenant devices via backend API
  const handleAddDiscoveredDevice = async (d) => {
    try {
      if (!selectedTenant?.id) {
        toast.error('Please select a tenant before adding devices');
        return;
      }
      const key = d.ip || `${d.hostname || 'device'}-${d.model || 'algo'}`;
      setAddingMap(prev => ({ ...prev, [key]: 'adding' }));

      const payload = {
        tenantId: selectedTenant.id,
        name: d.hostname || d.model || 'Algo Device',
        label: d.hostname || d.model || 'Algo Device',
        type: 'algo',
        deviceType: 'algo',
        model: d.model || '',
        agentId: agentId,
        // Provide common IP aliases to satisfy different backend normalizers
        ip_address: d.ip || '',
        ip: d.ip || '',
        ipAddress: d.ip || '',
        // Active flags (aliases)
        isActive: true,
        active: true,
        // MAC aliases when available
        mac: d.mac || '',
        macAddress: d.mac || '',
        mac_address: d.mac || '',
        capabilities: ['algo', 'inventory'],
        targets: {},
      };

      const res = await deviceApiFixed.create(payload, selectedTenant.id);
      if (res?.success || res?.data?.id) {
        setAddingMap(prev => ({ ...prev, [key]: 'added' }));
        const createdId = res?.data?.id || res?.id || 'unknown';
        console.log('[Devices] Create response:', res);
        toast.success(`Device added to tenant devices${createdId !== 'unknown' ? ` (id: ${createdId})` : ''}`);
        // If centralized provider is active, refresh provider-managed data
        try {
          if (typeof window !== 'undefined' && window.__ES_PROVIDER_ACTIVE && typeof refreshData === 'function') {
            await refreshData();
          }
        } catch (_) { /* noop */ }
      } else {
        setAddingMap(prev => ({ ...prev, [key]: undefined }));
        console.warn('[Devices] Create failed response:', res);
        const msg = res?.error || res?.message || 'Failed to add device';
        toast.error(msg);
      }
    } catch (err) {
      const key = d.ip || `${d.hostname || 'device'}-${d.model || 'algo'}`;
      setAddingMap(prev => ({ ...prev, [key]: undefined }));
      toast.error(err?.message || 'Failed to add device');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium text-gray-900">Agent Results: {agentId}</h3>
        <div className="flex items-center space-x-2">
          <span
            className={`inline-flex h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}
          ></span>
          <span className="text-xs text-gray-500">
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          <button
            onClick={clearResults}
            className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="bg-gray-50 rounded-md border border-gray-200 p-2 h-80 overflow-y-auto">
        {results.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <p>No results received yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {results.map((result, index) => (
              <div
                key={index}
                className={`p-3 rounded-md text-sm ${
                  result.success
                    ? 'bg-green-50 border border-green-100'
                    : 'bg-red-50 border border-red-100'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-medium">Task: </span>
                    <span className="font-mono">{result.taskId}</span>
                  </div>
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full ${
                      result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {result.success ? 'Success' : 'Failed'}
                  </span>
                </div>
                {(() => {
                  // Prefer structured data from MQTT if provided; fall back to parsing output JSON string
                  let tableData = result?.data || null;
                  if (!tableData) {
                    const parsed = parseMaybeJson(result?.output);
                    if (parsed) {
                      tableData = Array.isArray(parsed) ? { results: parsed } : parsed;
                    }
                  }
                  const hasResults = tableData && Array.isArray(tableData.results);
                  return hasResults;
                })() ? (
                  <div className="mt-2">
                    {/* Summary */}
                    <div className="text-xs text-gray-700 mb-2 flex flex-wrap gap-2">
                      <span>
                        <span className="font-medium">Devices:</span> {(result.data?.deviceCount ?? (() => {
                          const parsed = parseMaybeJson(result.output);
                          const td = parsed ? (Array.isArray(parsed) ? { results: parsed } : parsed) : result.data;
                          return td?.results?.length ?? 0;
                        })())}
                      </span>
                      <span>
                        <span className="font-medium">Algo:</span> {(result.data?.algoDeviceCount ?? (() => {
                          const parsed = parseMaybeJson(result.output);
                          const td = parsed ? (Array.isArray(parsed) ? { results: parsed } : parsed) : result.data;
                          return td?.results?.filter?.(d => d.isAlgoDevice)?.length ?? 0;
                        })())}
                      </span>
                      <span>
                        <span className="font-medium">Last Scan:</span> {formatTimestamp((() => {
                          const parsed = parseMaybeJson(result.output);
                          const td = result.data || (parsed ? (Array.isArray(parsed) ? { results: parsed } : parsed) : null);
                          return td?.lastScanTime;
                        })())}
                      </span>
                      <span>
                        <span className="font-medium">Scanning:</span> {String((() => {
                          const parsed = parseMaybeJson(result.output);
                          const td = result.data || (parsed ? (Array.isArray(parsed) ? { results: parsed } : parsed) : null);
                          return td?.isScanning;
                        })())}
                      </span>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-600">
                            <th className="px-2 py-1">IP</th>
                            <th className="px-2 py-1">Hostname</th>
                            <th className="px-2 py-1">Model</th>
                            <th className="px-2 py-1">Port</th>
                            <th className="px-2 py-1">Algo</th>
                            <th className="px-2 py-1">Firmware</th>
                            <th className="px-2 py-1">REST</th>
                            <th className="px-2 py-1">Login</th>
                            <th className="px-2 py-1">Last Seen</th>
                            <th className="px-2 py-1">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // Build the unified table data again for row rendering
                            const parsed = parseMaybeJson(result.output);
                            const td = result.data || (parsed ? (Array.isArray(parsed) ? { results: parsed } : parsed) : { results: [] });
                            return td.results;
                          })().map((d, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-2 py-1 font-mono">{d.ip}</td>
                              <td className="px-2 py-1">{d.hostname || '—'}</td>
                              <td className="px-2 py-1">{d.model || 'Unknown'}</td>
                              <td className="px-2 py-1">{d.port ?? '—'}</td>
                              <td className="px-2 py-1">{d.isAlgoDevice ? 'Yes' : 'No'}</td>
                              <td className="px-2 py-1">{d.firmware ?? '—'}</td>
                              <td className="px-2 py-1">{d.restEnabled === null || d.restEnabled === undefined ? '—' : d.restEnabled ? 'On' : 'Off'}</td>
                              <td className="px-2 py-1">{d.loginSuccess === null || d.loginSuccess === undefined ? '—' : d.loginSuccess ? 'Yes' : 'No'}</td>
                              <td className="px-2 py-1">{formatTimestamp(d.lastSeen)}</td>
                              <td className="px-2 py-1">
                                {d.isAlgoDevice ? (
                                  (() => {
                                    const key = d.ip || `${d.hostname || 'device'}-${d.model || 'algo'}`;
                                    const status = addingMap[key];
                                    const disabled = status === 'adding' || status === 'added';
                                    return status === 'added' ? (
                                      <span className="text-green-600">Added</span>
                                    ) : (
                                      <button
                                        onClick={() => handleAddDiscoveredDevice(d)}
                                        disabled={disabled}
                                        className={`px-2 py-1 rounded text-white ${disabled ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                                      >
                                        {status === 'adding' ? 'Adding...' : 'Add to Devices'}
                                      </button>
                                    );
                                  })()
                                ) : (
                                  '—'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="mt-1">
                    <span className="font-medium">Output: </span>
                    <span>{result.output}</span>
                  </div>
                )}
                <div className="mt-1 text-xs text-gray-500">
                  {formatTimestamp(result.timestamp)}
                </div>
              </div>
            ))}
          </div>
        )}
        <div ref={resultsEndRef} />
      </div>
    </div>
  );
};

AgentResultsPanel.propTypes = {
  agentId: PropTypes.string.isRequired,
};

export default AgentResultsPanel;
